export const GENERAL_GUIDANCE_NOTICES = {
  en: "General guidance only. Follow your care team's plan.",
  ar: "إرشادات عامة فقط. اتبع خطة فريقك الطبي.",
};

export const UNSUPPORTED_HISTORY_MESSAGES = {
  en: "I cannot read your food history yet. Please open History or Meal Mixer to review your logged foods. History access for the assistant is coming next.",
  ar: "لا أستطيع قراءة سجل الطعام بعد. يرجى فتح صفحة السجل أو حاسبة الوجبة لمراجعة الأطعمة المسجلة. ربط المساعد بالسجل قادم في الخطوة التالية.",
};

export const MEAL_BUILDER_TRIGGER_KEYWORDS = {
  en: [
    "i ate",
    "i had",
    "i mixed",
    "i prepared",
    "i made",
    "i cooked",
    "meal",
    "ingredients",
    "recipe",
    "tabbouleh",
  ],
  ar: ["أكلت", "اكلت", "حضرت", "حضّرت", "خلطت", "طبخت", "وجبة", "مكونات", "وصفة", "تبولة"],
};

export const HISTORY_INTENT_KEYWORDS = {
  en: ["check today", "check my history", "what did i eat today", "history", "today", "logged foods"],
  ar: ["سجل الطعام", "السجل", "أكلي اليوم", "اكلت اليوم", "أكلت اليوم", "راجع تاريخ اليوم"],
};

const ARABIC_TEXT_PATTERN = /[\u0600-\u06ff]/;
const HISTORY_INTENT_PATTERN =
  /\b(check\s+(today|my\s+history|history|my\s+totals)|check\s+my\s+totals\s+today|history|daily\s+summary|logged\s+foods|today'?s\s+totals|what\s+did\s+i\s+eat\s+today|what\s+have\s+i\s+eaten\s+today)\b|هل\s+يمكنك\s+مراجعة\s+أكلي\s+اليوم|شو\s+أكلت\s+اليوم|شو\s+اكلت\s+اليوم|راجع\s+أكلي\s+اليوم|راجع\s+اكلي\s+اليوم|راجع\s+تاريخ\s+اليوم|سجل\s+الطعام|السجل/iu;
const FOOD_LOG_ACTION_PATTERN =
  /\b(i\s+ate|i\s+had|i\s+logged|already\s+logged|log\s+.+(today|now|app|application))\b|أكلت|اكلت|لقد\s+سجلت|سجلت|سجلها|سجل\s+.+(اليوم|بالتطبيق|في\s+التطبيق)/iu;
const FOOD_DATABASE_QUESTION_PATTERN =
  /\b(what\s+(foods?|items?)\s+(are\s+)?(in|available)|food\s+database|database\s+foods?|trusted\s+food|verified\s+food|give\s+me\s+.+food|what\s+can\s+i\s+eat)\b|ماذا\s+تجد\s+في\s+قاعدة\s+الطعام\s+الحالية|قاعدة\s+الطعام|قاعدة\s+البيانات|طعاما\s+موثوقا|طعاماً\s+موثوقاً|اعطني\s+طعاما|اعطني\s+طعاماً|شو\s+فيني\s+أكل\s+اليوم|شو\s+فيني\s+اكل\s+اليوم/iu;
const MEAL_ACTION_PATTERN =
  /\b(i\s+ate|i\s+had|i\s+mixed|i\s+prepared|i\s+made|i\s+cooked|ate|had|mixed|prepared|made|cooked)\b|أكلت|اكلت|حضرت|حضّرت|خلطت|طبخت/iu;
const MEAL_CONTEXT_PATTERN = /\b(meal|ingredients?|recipe|tabbouleh|mixed\s+food)\b|وجبة|مكونات|وصفة|تبولة/iu;
const FOOD_COMBINATION_PATTERN =
  /\b(rice|chicken|cucumber|parsley|tomato|bulgur|mint|lemon|olive\s+oil)\b[\s\S]{0,50}\b(with|and|plus)\b[\s\S]{0,50}\b(rice|chicken|cucumber|parsley|tomato|bulgur|mint|lemon|olive\s+oil)\b|رز[\s\S]{0,50}(مع|و)[\s\S]{0,50}(دجاج|خيار)|دجاج[\s\S]{0,50}(مع|و)[\s\S]{0,50}(رز|خيار)/iu;
const FOOD_NAME_PATTERN =
  /\b(rice|chicken|cucumber|parsley|tomato|bulgur|mint|lemon|olive\s+oil|apple|pineapple|banana)\b|رز|أرز|ارز|دجاج|خيار|بقدونس|طماطم|بندورة|برغل|نعناع|ليمون|زيت\s+زيتون|تفاحة|تفاح|أناناس|اناناس|موزة|موز/iu;
const KNOWN_MEAL_PATTERN = /\btabbouleh\b|تبولة/iu;

export const getQuestionLanguage = (question) => (ARABIC_TEXT_PATTERN.test(String(question || "")) ? "ar" : "en");

export const isHistoryIntent = (question) => HISTORY_INTENT_PATTERN.test(String(question || ""));

export const isFoodLogActionIntent = (question) => {
  const text = String(question || "").trim();
  if (!text || isHistoryIntent(text)) return false;
  return FOOD_LOG_ACTION_PATTERN.test(text) && FOOD_NAME_PATTERN.test(text);
};

export const isFoodDatabaseQuestionIntent = (question) => {
  const text = String(question || "").trim();
  if (!text || isHistoryIntent(text) || isFoodLogActionIntent(text)) return false;
  return FOOD_DATABASE_QUESTION_PATTERN.test(text);
};

export const getAssistantIntent = (question) => {
  if (isHistoryIntent(question)) return "history";
  if (isFoodLogActionIntent(question)) return "food_log_action";
  if (isFoodDatabaseQuestionIntent(question)) return "food_database_question";
  if (isMealBuilderIntent(question)) return "meal_builder";
  return "general";
};

export const isMealBuilderIntent = (question) => {
  const text = String(question || "").trim();
  if (!text) return false;

  const isHistoryQuestion = isHistoryIntent(text);
  const hasMealSignal =
    KNOWN_MEAL_PATTERN.test(text) ||
    FOOD_COMBINATION_PATTERN.test(text) ||
    (MEAL_ACTION_PATTERN.test(text) && FOOD_NAME_PATTERN.test(text)) ||
    (MEAL_ACTION_PATTERN.test(text) && MEAL_CONTEXT_PATTERN.test(text));

  if (isHistoryQuestion && !hasMealSignal) return false;
  return hasMealSignal;
};

export const getUnsupportedHistoryMessage = (question) =>
  UNSUPPORTED_HISTORY_MESSAGES[getQuestionLanguage(question)];
